// ** Redux Imports
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'

// ** Axios Imports
import axios from 'axios'

import { orderBy, flatten, head } from 'lodash'

import { isDurationTypeOverride } from '../../../@core/enums/rateDurationType'

export const getRates = createAsyncThunk('appRates/getRates', async () => {
  const response = await axios.get(`/v1/rates`)
  return response.data
})

export const updateRates = createAsyncThunk('appRates/updateRates', async ({ customerId, changedRates }) => {
  const response = await axios.post(`/v1/rates`, { customerId,
    changedRates: changedRates.map(r => ({
      id: r.id,
      rates: { ...r.rates }
    }))
  })
  return response.data
})

export const getRateHistory = createAsyncThunk('appRates/getRateHistory', async (customerId) => {
  const response = await axios.get(`/v1/rates/history/${customerId}`)
  return response.data
})

// Correct an existing active rate — supersedes the original, keeping it in history.
export const correctRate = createAsyncThunk('appRates/correctRate', async ({ rateId, amount, effectiveFrom, currency, durationType, reason }) => {
  const response = await axios.put(`/v1/rates/${rateId}/correct`, { amount, effectiveFrom, currency, durationType, reason })
  return response.data
})

// Void (soft-delete) a rate that should not exist. The reason travels in the
// request body — unusual for a DELETE, but it belongs on the audit snapshot and
// there is nowhere else to put it.
export const voidRate = createAsyncThunk('appRates/voidRate', async ({ rateId, reason }) => {
  const response = await axios.delete(`/v1/rates/${rateId}`, { data: { reason } })
  return response.data
})

// What the rate is currently worth on an invoice, so the correct-rate dialog can
// state the consequence of a correction instead of warning about it in prose.
//
// Advisory by contract: resolves to null on any failure rather than rejecting.
// A missing figure must degrade a sentence, never block the correction — so this
// deliberately does not go through the slice's rejected handling.
export const getRateImpact = async (rateId) => {
  try {
    const response = await axios.get(`/v1/rates/${rateId}/impact`)
    return response.data
  } catch {
    return null
  }
}

const mapCustomers = (customers) => {
  return customers.map(customer => {
    const allRates = flatten((customer.users || []).map(u => u.rates || []))

    // The header selector shows the customer's *prevailing* payment type, so a
    // single overridden employee must not skew it — otherwise one cost-plus
    // engineer would make the whole account read "Cost Plus".
    const inheritedRates = allRates.filter(r => !isDurationTypeOverride(r.durationType))
    const rateDurationType = head(orderBy(inheritedRates, r => r.effectiveFrom, 'desc'))?.durationType || 1
    const currencyType = head(orderBy(allRates, r => r.effectiveFrom, 'desc'))?.currency || 1

    return {
      ...customer,
      rateDurationType,
      currencyType,
      users: (customer.users || []).map(u => {
        const rates = orderBy(u.rates, r => new Date(r.effectiveFrom), 'desc')
        const currentRate = rates.find(r => new Date(r.effectiveFrom) < new Date())
        const newRate = rates.find(r => new Date(r.effectiveFrom) >= new Date())
        return {
          ...u,
          // The raw list, oldest first — `rates` below replaces the API array
          // with the current/next summary, and the phone view (30c) draws the
          // person's history as chips from this.
          rateHistory: [...rates].reverse(),
          rates: {
            currentRateId: currentRate?.id ?? null,
            currentRate: currentRate?.amount,
            currentCurrency: currentRate?.currency || currencyType,
            // Payment type is a per-row property; carry both the active rate's
            // and the future draft's so a row can differ from the customer default.
            currentDurationType: currentRate?.durationType ?? rateDurationType,
            lastReviewDate: currentRate ? new Date(currentRate.effectiveFrom) : null,
            newRateId: newRate?.id ?? null,
            newRate: newRate?.amount,
            newCurrency: newRate?.currency || currencyType,
            newDurationType: newRate?.durationType ?? (currentRate?.durationType ?? rateDurationType),
            nextReviewDate: newRate ? new Date(newRate.effectiveFrom) : null
          }
        }
      })
    }
  })
}

export const appRatesSlice = createSlice({
  name: 'rates',
  initialState: {
    data: [],
    isLoading: false,
    updatingRates: false,
    history: null,
    historyLoading: false
  },
  reducers: {
    clearHistory: (state) => {
      state.history = null
    }
  },
  extraReducers: builder => {
    builder
      .addCase(getRates.pending, (state) => {
        state.isLoading = true
        state.data = []
      })
      .addCase(getRates.fulfilled, (state, action) => {
        state.data = mapCustomers(action.payload)
        state.isLoading = false
      })
      .addCase(updateRates.pending, (state) => {
        state.updatingRates = true
      })
      .addCase(updateRates.fulfilled, (state, action) => {
        state.data = mapCustomers(action.payload)
        state.updatingRates = false
      })
      // Without this a failed save left `updatingRates` true for the rest of
      // the session: the header spinner never stopped and the phone strip
      // (which disables Cancel while saving) could not be closed.
      .addCase(updateRates.rejected, (state) => {
        state.updatingRates = false
      })
      .addCase(correctRate.pending, (state) => {
        state.updatingRates = true
      })
      .addCase(correctRate.fulfilled, (state, action) => {
        state.data = mapCustomers(action.payload)
        state.updatingRates = false
      })
      .addCase(correctRate.rejected, (state) => {
        state.updatingRates = false
      })
      .addCase(voidRate.pending, (state) => {
        state.updatingRates = true
      })
      .addCase(voidRate.fulfilled, (state, action) => {
        state.data = mapCustomers(action.payload)
        state.updatingRates = false
      })
      .addCase(voidRate.rejected, (state) => {
        state.updatingRates = false
      })
      .addCase(getRateHistory.pending, (state) => {
        state.historyLoading = true
      })
      .addCase(getRateHistory.fulfilled, (state, action) => {
        state.history = action.payload
        state.historyLoading = false
      })
      .addCase(getRateHistory.rejected, (state) => {
        state.historyLoading = false
      })
  }
})

export const { clearHistory } = appRatesSlice.actions

export default appRatesSlice.reducer
